import { cx } from '@linaria/core';
import { useRef } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { TabUnderline, UnderlineDirections, Tooltip, Button } from '@ui/kit';
import { CSSRefTransition, useLoaderState } from '@utils/react';
import { SelectTrigger } from '../../../../molecules/select-panel/select-trigger.js';
import { useSelectedCourse } from '../../../../state/selectors.js';
import { shareStore } from './_share-drawer.state.js';
import { headingCls } from './_share-drawer.style.js';
import { wrapperCls, tabsCls, tabToggleCls, tabUnderlineCls, tabCls, tabTransitionDurationMs, shareButtonCls, tabMessageCls } from './share-section.style.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import { useShareWithStudentsMutation } from '../../../../api/mutation.share-with-students.js';
import { useShareWithTeachersMutation } from '../../../../api/mutation.share-with-teachers.js';

function ShareSection(props) {
  const state = shareStore.useSelector(state => state);
  const selectedCourse = useSelectedCourse();
  const shareWithStudents = useShareWithStudentsMutation();
  const shareWithTeachers = useShareWithTeachersMutation();
  const tabsRef = useRef(null);
  const activeTabIndex = state.selectedTab === "classes" ? 0 : 1;

  // We can submit based on two different scenarios:
  // 1) If the active tab is classes and at least a class has been selected and
  // the list of students has been loaded
  // 2) If the active tab is coworkers and at least one coworker has been
  // selected.
  const canSubmit = state.selectedTab === "classes" ? !!state.selectedClass && typeof state.studentsInClass !== "undefined" : (state.selectedCoWorkers?.length ?? 0) > 0;
  const handleSubmit = async evt => {
    if (!canSubmit || !selectedCourse) {
      return;
    }

    // Immediately close the panel and perform the sharing
    props.onClose(evt);
    if (state.selectedTab === "classes") {
      const students = state.selectedStudents?.length ? state.selectedStudents : state.studentsInClass;
      await shareWithStudents.mutateAsync({
        course: selectedCourse,
        studentIds: students?.map(it => it.id) ?? []
      });
    } else if (state.selectedCoWorkers) {
      await shareWithTeachers.mutateAsync({
        course: selectedCourse,
        teacherIds: state.selectedCoWorkers.map(it => it.id)
      });
    }

    // ... and clear the panel on success
    shareStore.dispatch(shareStore.actions.reset());
  };
  return /*#__PURE__*/jsxs("section", {
    className: wrapperCls,
    children: [/*#__PURE__*/jsx("h1", {
      className: headingCls,
      children: "Del med elever og kollegaer"
    }), /*#__PURE__*/jsxs("div", {
      ref: tabsRef,
      className: tabsCls,
      children: [/*#__PURE__*/jsx("button", {
        className: cx(tabToggleCls, state.selectedTab === "classes" && "active"),
        onClick: () => shareStore.dispatch(shareStore.actions.changeTab("classes")),
        children: "Hold og elever"
      }), /*#__PURE__*/jsx("button", {
        className: cx(tabToggleCls, state.selectedTab === "coWorkers" && "active"),
        onClick: () => shareStore.dispatch(shareStore.actions.changeTab("coWorkers")),
        children: "Kollegaer"
      }), /*#__PURE__*/jsx(TabUnderline, {
        className: tabUnderlineCls,
        direction: UnderlineDirections.HORIZONTAL,
        parentElementRef: tabsRef,
        activeItemIndex: activeTabIndex,
        disableInitialTransition: true
      })]
    }), /*#__PURE__*/jsxs(TransitionGroup, {
      children: [state.selectedTab === "classes" && /*#__PURE__*/jsx(CSSRefTransition, {
        timeout: tabTransitionDurationMs,
        classNames: tabCls,
        children: ref => /*#__PURE__*/jsx(ClassesTab, {
          ref: ref
        })
      }, "classes"), state.selectedTab === "coWorkers" && /*#__PURE__*/jsx(CSSRefTransition, {
        timeout: tabTransitionDurationMs,
        classNames: tabCls,
        children: ref => /*#__PURE__*/jsx(CoWorkersTab, {
          ref: ref
        })
      }, "coWorkers")]
    }), /*#__PURE__*/jsx(Tooltip, {
      placement: "top",
      body: "Du mangler at v\xE6lge hvem du vil dele forl\xF8bet med",
      ariaType: "description",
      disabled: canSubmit,
      children: ref => /*#__PURE__*/jsx(Button, {
        ref: ref,
        theme: "primary",
        className: shareButtonCls,
        "aria-disabled": !canSubmit,
        onClick: handleSubmit,
        children: "Del"
      })
    })]
  });
}
function ClassesTab(props) {
  const selectedClass = shareStore.useSelector(state => state.selectedClass);
  const selectedStudents = shareStore.useSelector(state => state.selectedStudents);
  const studentsInClass = shareStore.useSelector(state => state.studentsInClass);
  const canSelectStudents = !!selectedClass;
  const isLoadingStudents = canSelectStudents && !studentsInClass;
  const showThrobber = useLoaderState(isLoadingStudents, {
    delayMs: 350,
    durationMs: 500
  });
  return /*#__PURE__*/jsxs("div", {
    ref: props.ref,
    className: tabCls,
    children: [/*#__PURE__*/jsx(SelectTrigger, {
      label: "Hold",
      valuePlaceholder: "V\xE6lg",
      selectedValues: selectedClass ? [selectedClass.name] : [],
      onClick: () => shareStore.dispatch(shareStore.actions.changeSelector("class"))
    }), /*#__PURE__*/jsx(SelectTrigger, {
      label: canSelectStudents ? "Elever" : "Elever (vælg hold først)",
      valuePlaceholder: canSelectStudents ? "Alle elever" : "Vælg",
      selectedValues: selectedStudents?.map(it => it.name),
      onClick: () => shareStore.dispatch(shareStore.actions.changeSelector("student")),
      disabled: !canSelectStudents || isLoadingStudents,
      loading: showThrobber.current
    })]
  });
}
function CoWorkersTab(props) {
  const selectedCoWorkers = shareStore.useSelector(state => state.selectedCoWorkers);
  return /*#__PURE__*/jsxs("div", {
    ref: props.ref,
    className: tabCls,
    children: [/*#__PURE__*/jsx(SelectTrigger, {
      label: "Kollega",
      valuePlaceholder: "V\xE6lg kollega",
      selectedValues: selectedCoWorkers?.map(it => it.name),
      onClick: () => shareStore.dispatch(shareStore.actions.changeSelector("coWorker"))
    }), /*#__PURE__*/jsx("p", {
      className: tabMessageCls,
      children: "Din kollega vil modtage en redigerbar kopi"
    })]
  });
}

export { ShareSection };
//# sourceMappingURL=share-section.js.map
